import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '@/store/useUserStore';
import { apiCache } from '@/lib/cacheCleanup';

/**
 * Query-key prefixes (first array element) that should NOT be invalidated when
 * a super admin switches workspaces. These queries hit the master DB
 * (`peopleaps_master`) only and are workspace-agnostic, so keeping their
 * cached data avoids pointless refetches.
 *
 * To opt out of workspace-switch invalidation for a new master-only feature,
 * add its top-level queryKey here (e.g. `useQuery({ queryKey: ['workspaces'] })`
 * already qualifies).
 */
export const MASTER_ONLY_QUERY_KEYS: ReadonlySet<string> = new Set([
  'workspaces',         // /workspaces — list of all workspaces
  'sbl-khas',           // /sbl-khas  — SBL Khas codes
  'packages',           // /packages  — package catalog
  'organizational-unit-types', // master-DB OU type registry
]);

/**
 * Reloads tenant-scoped data when the super admin picks a different workspace
 * from the navbar dropdown.
 *
 * Mechanism:
 *  - Clears the custom HTTP `apiCache` (used by `src/lib/api.ts`) so any
 *    non-React-Query consumer can't hand back stale data from the previous
 *    workspace.
 *  - Invalidates every React Query EXCEPT those whose top-level key is listed
 *    in `MASTER_ONLY_QUERY_KEYS`. Mounted queries refetch automatically via
 *    React Query's stale-invalidation flow, so no per-component wiring is
 *    needed.
 *
 * Future-proofing: new workspace-scoped hooks need to do nothing. New master-
 * only hooks just need their query key added to `MASTER_ONLY_QUERY_KEYS`.
 *
 * Mount once near the root of the authenticated app (PortalLayout).
 */
export const useWorkspaceSwitchReload = () => {
  const queryClient = useQueryClient();
  const selectedWorkspaceId = useUserStore((s) => s.selectedWorkspaceId);
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin);
  const prevWorkspaceIdRef = useRef<number | null>(null);

  useEffect(() => {
    const prev = prevWorkspaceIdRef.current;
    prevWorkspaceIdRef.current = selectedWorkspaceId;

    if (!isSuperAdmin) return;

    // Skip first mount and the bootstrap transition (null → first id). We
    // only want to react to a genuine, user-initiated switch between two
    // workspaces.
    if (prev === null || prev === selectedWorkspaceId || selectedWorkspaceId === null) {
      return;
    }

    apiCache.clear();

    queryClient.invalidateQueries({
      predicate: (query) => {
        const firstKey = query.queryKey[0];
        if (typeof firstKey !== 'string') return true;
        return !MASTER_ONLY_QUERY_KEYS.has(firstKey);
      },
    });

    console.log(`[Workspace] Switched ${prev} → ${selectedWorkspaceId}, tenant data invalidated`);
  }, [selectedWorkspaceId, isSuperAdmin, queryClient]);
};
